// Laminated wooden frame, shared by the 3D preview and the backend cutting files (CommonJS so plain node loads it).
// A stack of flat rings glued face to face, millimetres, origin = outer corner, y down. The front ring is mitred;
// the plies under it alternate long top/bottom rails with long side rails, so every corner is lapped by the plies
// around it. Rails longer than the sheet are split, staggered (m segments, then m + 1) so no joint sits over
// another; the front keeps the fewest, symmetric. Ring 0 is wider: a back lip the backplate rests on.
const FRAME_RAIL_MM = 15.4;
const FRAME_LIP_MM = 6;
const FRAME_CLEARANCE_MM = 0.5;
// the frame stands this far proud of the top ply
const FRAME_REVEAL_MM = 6;
// the cutting packer's grid; a rail must fit the sheet after rounding up to it
const GRID_MM = 1;

// `stackPlies`: backplate + cut plies of the map
function frameRings({
    boardMm,
    stackPlies,
    sheetMm = { width: 297, height: 210 },
    plyMm = 4,
    gapMm = 2,
    railMm = FRAME_RAIL_MM,
    lipMm = FRAME_LIP_MM,
    clearanceMm = FRAME_CLEARANCE_MM,
    revealMm = FRAME_REVEAL_MM,
}) {
    const count = 1 + Math.ceil((stackPlies * plyMm + revealMm) / plyMm);
    const OW = boardMm.width + 2 * (clearanceMm + railMm);
    const OH = boardMm.height + 2 * (clearanceMm + railMm);
    const maxLen = Math.max(sheetMm.width, sheetMm.height) - 2 * gapMm - 2 * GRID_MM;

    const rings = Array.from({ length: count }, (_, k) => {
        const w = k === 0 ? railMm + clearanceMm + lipMm : railMm;
        const fromFront = count - 1 - k;
        const style = fromFront === 0 ? 'mitre' : fromFront % 2 ? 'h' : 'v';
        const rails = [];
        const seams = [];
        [
            ['T', OW, style !== 'v'],
            ['B', OW, style !== 'v'],
            ['L', OH, style !== 'h'],
            ['R', OH, style !== 'h'],
        ].forEach(([side, length, full]) => {
            const a = full ? 0 : w;
            const b = full ? length : length - w;
            const m = Math.ceil((b - a) / maxLen);
            const segments = m > 1 && fromFront % 2 ? m + 1 : m;
            const cuts = Array.from({ length: segments + 1 }, (_, j) => a + ((b - a) * j) / segments);
            // a point at `s` along the rail axis, `d` in from the outer edge
            const at = (s, d) => ({ T: [s, d], B: [s, OH - d], L: [d, s], R: [OW - d, s] })[side];
            for (let j = 0; j < segments; j++) {
                const [s0, s1] = [cuts[j], cuts[j + 1]];
                const i0 = s0 + (style === 'mitre' && j === 0 ? w : 0);
                const i1 = s1 - (style === 'mitre' && j === segments - 1 ? w : 0);
                rails.push({
                    id: `F${k + 1}${side}${segments > 1 ? j + 1 : ''}`,
                    side,
                    ring: [at(s0, 0), at(s1, 0), at(i1, w), at(i0, w)],
                    at,
                    span: [s0, s1],
                });
                if (j) seams.push([at(s0, 0), at(s0, w)]);
            }
        });
        // corner joints, one per corner
        [
            [0, 0, 1, 1],
            [OW, 0, -1, 1],
            [0, OH, 1, -1],
            [OW, OH, -1, -1],
        ].forEach(([x, y, dx, dy]) => {
            if (style === 'mitre') seams.push([[x, y], [x + dx * w, y + dy * w]]);
            else if (style === 'h') seams.push([[x, y + dy * w], [x + dx * w, y + dy * w]]);
            else seams.push([[x + dx * w, y], [x + dx * w, y + dy * w]]);
        });
        return { w, rails, seams };
    });
    return { rings, outerMm: { width: OW, height: OH }, depthMm: count * plyMm };
}

module.exports = { FRAME_RAIL_MM, frameRings };
